import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { getLoggedInUser, logout } from "../helpers/authHelper";
import { useWorkspaceAccess } from "./WorkspaceAccess";

const navigationSections = [
  {
    title: "Main",
    items: [
      ["Dashboard", "/dashboard", "ti-layout-dashboard"],
      ["Content Calendar", "/content-calendar", "ti-calendar-event"],
      ["AI Post Studio", "/ai-post-studio", "ti-sparkles"],
      ["Manual Post Studio", "/manual-post-studio", "ti-pencil"],
      ["Media Library", "/media-library", "ti-photo"],
      ["Social Accounts", "/social-accounts", "ti-brand-instagram"],
      ["Messages & Automations", "/messages", "ti-message-circle"],
      ["Leads", "/leads", "ti-user-star"],
      ["Analytics", "/analytics", "ti-chart-bar"],
    ],
  },
  {
    title: "Business",
    items: [
      ["Business Profile", "/business-profile", "ti-building-store"],
    ],
  },
  {
    title: "Account",
    items: [
      ["Notifications", "/notifications", "ti-bell"],
      ["Help & Support", "/help-support", "ti-lifebuoy"],
      ["Settings", "/settings", "ti-settings"],
    ],
  },
];

const WorkspaceSidebar = ({ collapsed, unreadCount = 0, onToggleCollapsed, onNavigate }) => {
  const navigate = useNavigate();
  const { access, canOpen, firstAllowedPath } = useWorkspaceAccess();
  // Until permissions load, show nothing rather than links that might not be allowed.
  const visibleSections = navigationSections
    .map((section) => ({ ...section, items: access ? section.items.filter(([, to]) => canOpen(to)) : [] }))
    .filter((section) => section.items.length);
  const [user, setUser] = useState(getLoggedInUser);
  useEffect(() => {
    const refreshUser = () => setUser(getLoggedInUser());
    window.addEventListener("auth:user-updated", refreshUser);
    window.addEventListener("storage", refreshUser);
    return () => {
      window.removeEventListener("auth:user-updated", refreshUser);
      window.removeEventListener("storage", refreshUser);
    };
  }, []);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Workspace user";
  const initials = displayName.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside id="workspace-sidebar" className="workspace-sidebar" aria-label="Main navigation">
      <div className="workspace-sidebar-brand">
        <NavLink to={access && !canOpen("/dashboard") ? firstAllowedPath : "/dashboard"} onClick={onNavigate} className="workspace-brand-link" aria-label="Socialyorix workspace home">
          <img className="workspace-brand-mark" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-mark.png`} alt="" />
          <span className="workspace-brand-name"><img className="workspace-brand-logo" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-logo.png`} alt="" /></span>
        </NavLink>
        <button
          className="workspace-collapse-button"
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <i className={`ti ${collapsed ? "ti-layout-sidebar-right-expand" : "ti-layout-sidebar-left-collapse"}`} aria-hidden="true" />
        </button>
      </div>

      <nav className="workspace-nav">
        {visibleSections.map((section) => (
          <div className="workspace-nav-section" key={section.title}>
            <h2 className="workspace-nav-heading">{section.title}</h2>
            {section.items.map(([label, to, icon]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/dashboard"}
                onClick={onNavigate}
                className={({ isActive }) => `workspace-nav-link${isActive ? " is-active" : ""}`}
                aria-label={label}
                title={label}
                data-tooltip={label}
              >
                <i className={`ti ${icon}`} aria-hidden="true" />
                <span className="workspace-nav-label">{label}</span>
                {to === "/notifications" && unreadCount > 0 && (
                  <span className="workspace-sidebar-notification-count" aria-label={`${unreadCount} unread notifications`}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="workspace-sidebar-bottom">
        {access && canOpen("/upgrade-plan") && <NavLink to="/upgrade-plan" onClick={onNavigate} className="workspace-upgrade-link" aria-label="Upgrade Plan" title="Upgrade Plan" data-tooltip="Upgrade Plan">
          <i className="ti ti-rocket" aria-hidden="true" />
          <span className="workspace-nav-label">Upgrade Plan</span>
          <i className="ti ti-arrow-up-right workspace-upgrade-indicator" aria-hidden="true" />
        </NavLink>}
        <NavLink to="/account/profile" onClick={onNavigate} className="workspace-profile-link" aria-label={`User Profile: ${displayName}`} title={displayName} data-tooltip={displayName}>
          <span className="workspace-avatar" aria-hidden="true">{initials || "U"}</span>
          <span className="workspace-profile-copy"><strong>{displayName}</strong><small>Account profile</small></span>
          <i className="ti ti-chevron-right workspace-profile-chevron" aria-hidden="true" />
        </NavLink>
        <button className="workspace-logout-link" type="button" onClick={handleLogout} aria-label="Logout" title="Logout" data-tooltip="Logout">
          <i className="ti ti-logout-2" aria-hidden="true" />
          <span className="workspace-nav-label">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default WorkspaceSidebar;
